import { curSym } from "@/lib/company";
import { DatePickerTR } from "@/components/DatePickerTR";
import { useEffect, useState, useCallback, useRef } from "react";
import api, { apiError } from "@/lib/api";
import { fmtTL } from "@/components/StatusBadge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Coins, Users, Wallet, TrendingUp, Building2, FileText, Trash2, Receipt, Settings2, Landmark, Percent, RotateCcw, Loader2, Printer, FileDown, FileSpreadsheet, Mail, FileBarChart, Archive, AlertTriangle, Eye } from "lucide-react";
import { useSort } from "@/lib/sort";
import { SortTh } from "@/components/SortTh";
import { useActiveLocation } from "@/context/ActiveLocationContext";
import { useAuth } from "@/context/AuthContext";
import { Kpi } from "@/pages/accounting/shared";
import { EntityAvatar, AvatarUpload } from "@/components/EntityAvatar";
import EntityDetailDialog from "@/components/EntityDetailDialog";
import { BANKS, BankBadge } from "@/lib/banks";

const downloadBlob = async (url, filename) => {
  try {
    const res = await api.get(url, { responseType: "blob" });
    const href = window.URL.createObjectURL(new Blob([res.data]));
    const a = document.createElement("a");
    a.href = href; a.download = filename;
    document.body.appendChild(a); a.click(); a.remove();
    window.URL.revokeObjectURL(href);
  } catch (e) { toast.error("İndirme başarısız (yetki/izin olmayabilir)"); }
};

export default function PersonnelTab({ canWrite, openNew, setOpenNew }) {
  const { locations: locs } = useActiveLocation();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [data, setData] = useState({ items: [], total_monthly_salary: 0 });
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [viewing, setViewing] = useState(null);
  const [viewDocs, setViewDocs] = useState([]);
  const [period, setPeriod] = useState(() => new Date().toISOString().slice(0, 7));
  const [pay, setPay] = useState(null);
  const [payslip, setPayslip] = useState(null);      // {pid} maaş pusulası dialogu
  const [runOpen, setRunOpen] = useState(false);      // bordro çalıştır dialogu
  const [paramsOpen, setParamsOpen] = useState(false); // 2026 parametre editörü
  const [annualOpen, setAnnualOpen] = useState(false); // yıllık icmal dialogu
  const [advancesOpen, setAdvancesOpen] = useState(false); // personel avans özeti
  const { sortKey, sortDir, toggle: onSort, sortRows } = useSort();
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const load = useCallback(() => {
    setLoading(true);
    api.get("/accounting/personnel", { params: { status: "all" } }).then((r) => setData(r.data || { items: [] })).catch(() => {}).finally(() => setLoading(false));
  }, []);
  const loadPay = useCallback(() => {
    api.get("/accounting/payroll/summary", { params: { period } }).then((r) => setPay(r.data)).catch(() => {});
  }, [period]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadPay(); }, [loadPay]);
  useEffect(() => {
    const size = pageSize === "all" ? (data.items.length || 1) : pageSize;
    const tp = Math.max(1, Math.ceil((data.items.length || 0) / size));
    if (page > tp) setPage(tp);
  }, [data.items.length, pageSize, page]);
  useEffect(() => { if (openNew) { setEditing({}); if (setOpenNew) setOpenNew(false); } }, [openNew, setOpenNew]);

  const del = async (id) => {
    if (!window.confirm("Personel silinsin mi? Geçmiş maaş kayıtları defterde kalır.")) return;
    try { await api.delete(`/accounting/personnel/${id}`); toast.success("Personel silindi"); load(); loadPay(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const [bulkEmailing, setBulkEmailing] = useState(false);
  const [bulkResult, setBulkResult] = useState(null);
  const bulkEmailPayslips = async () => {
    if (!window.confirm(`${pay?.period_label || period} dönemine ait tüm maaş pusulaları, e-postası olan personele gönderilecek. Onaylıyor musunuz?`)) return;
    setBulkEmailing(true);
    try {
      const res = await api.post(`/accounting/payroll/payslips/bulk-email?period=${period}`);
      const d = res.data || {};
      setBulkResult(d);
      toast.success(`${d.sent_count || 0} pusula gönderildi${d.skipped_count ? ` · ${d.skipped_count} atlandı (e-posta yok)` : ""}${d.failed_count ? ` · ${d.failed_count} başarısız` : ""}`);
    } catch (e) { toast.error(apiError(e.response?.data?.detail) || "Toplu e-posta gönderilemedi"); }
    finally { setBulkEmailing(false); }
  };

  const [orphans, setOrphans] = useState(null);
  const [archiving, setArchiving] = useState(false);
  const loadOrphans = useCallback(() => {
    api.get("/accounting/payroll/orphans").then((r) => setOrphans(r.data)).catch(() => {});
  }, []);
  useEffect(() => { loadOrphans(); }, [loadOrphans]);
  const archiveOrphans = async () => {
    if (!window.confirm("Silinmiş personele ait tüm eski bordro kayıtları arşivlenecek (muhasebe defterinden SİLİNMEZ, yalnızca listelerden gizlenir). Onaylıyor musunuz?")) return;
    setArchiving(true);
    try {
      const r = await api.post("/accounting/payroll/archive-orphans");
      toast.success(`${r.data.archived || 0} silinmiş personel kaydı arşivlendi`);
      loadOrphans(); loadPay(); setBulkResult(null);
    } catch (e) { toast.error(apiError(e.response?.data?.detail) || "Arşivlenemedi"); }
    finally { setArchiving(false); }
  };

  const [archivedOpen, setArchivedOpen] = useState(false);
  const [archived, setArchived] = useState(null);
  const loadArchived = useCallback(() => {
    api.get("/accounting/payroll/archived-orphans").then((r) => setArchived(r.data)).catch(() => {});
  }, []);
  useEffect(() => { loadArchived(); }, [loadArchived]);
  const restoreOrphans = async (personnelId) => {
    const label = personnelId ? "Bu personelin arşivlenmiş bordro kayıtları geri alınacak." : "TÜM arşivlenmiş bordro kayıtları geri alınacak.";
    if (!window.confirm(`${label} Geri alınca tekrar listelerde/pusulalarda görünür. Onaylıyor musunuz?`)) return;
    try {
      const r = await api.post("/accounting/payroll/restore-orphans", personnelId ? { personnel_id: personnelId } : {});
      toast.success(`${r.data.restored || 0} bordro kaydı geri alındı`);
      loadArchived(); loadOrphans(); loadPay();
    } catch (e) { toast.error(apiError(e.response?.data?.detail) || "Geri alınamadı"); }
  };

  const paidMap = {};
  (pay?.items || []).forEach((p) => { paidMap[p.personnel_id] = p.paid; });
  const cols = {
    code: (p) => p.code || "", name: (p) => p.name || "", title: (p) => p.title || "",
    location: (p) => p.location_name || "", salary: (p) => Number(p.monthly_salary) || 0,
    account: (p) => p.account || "", active: (p) => (p.active === false ? 0 : 1),
  };
  const payByPid = {};
  (pay?.items || []).forEach((r) => { payByPid[r.personnel_id] = r; });

  return (
    <div className="space-y-4" data-testid="personnel-tab">
      {orphans?.count > 0 && canWrite && (
        <div data-testid="orphan-payroll-banner" className="flex flex-wrap items-center justify-between gap-3 rounded-md border border-amber-200 bg-amber-50 px-4 py-3">
          <div className="flex items-start gap-2 text-sm text-amber-800">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
            <span><b>{orphans.personnel_count}</b> silinmiş personele ait <b>{orphans.count}</b> eski bordro kaydı listeleri kirletiyor (toplu e-posta/pusulalarda "Silinmiş personel" olarak görünür). Muhasebe defterinden silinmez, yalnızca gizlenir.</span>
          </div>
          <Button data-testid="orphan-archive-btn" size="sm" disabled={archiving} onClick={archiveOrphans} className="bg-amber-600 text-white hover:bg-amber-700">
            {archiving ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Archive className="mr-1.5 h-4 w-4" />} Arşivle
          </Button>
        </div>
      )}
      <div className="rounded-md bg-white p-4 ring-1 ring-inset ring-slate-200" data-testid="payroll-card">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <p className="overline">Bordro · {pay?.params_year || 2026} Parametreleri</p>
            <p className="mt-1 text-xs text-slate-500">Türkiye standardı brütten-nete bordro: SGK işçi/işveren, işsizlik, gelir vergisi (kümülatif dilim), damga vergisi, asgari ücret vergi/damga istisnası, yol & yemek istisnası. <b>İşveren maliyeti</b> gidere işlenir. Aynı ay mükerrer kayıt oluşmaz.</p>
          </div>
          <div className="flex flex-wrap items-end gap-2">
            <div>
              <label className="mb-1 block text-xs text-slate-500">Dönem</label>
              <input data-testid="payroll-period" type="month" value={period} onChange={(e) => setPeriod(e.target.value)}
                className="h-9 rounded-md border border-input bg-white px-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-green" />
            </div>
            <Button data-testid="payroll-annual-btn" variant="outline" onClick={() => setAnnualOpen(true)} className="border-slate-300">
              <FileBarChart className="mr-1.5 h-4 w-4" /> Yıllık İcmal
            </Button>
            <Button data-testid="payroll-sgk-btn" variant="outline" onClick={() => downloadBlob(`/accounting/payroll/monthly-sgk/export?period=${period}&format=xlsx`, `sgk-muhtasar-icmal_${period}.xlsx`)} className="border-slate-300">
              <FileSpreadsheet className="mr-1.5 h-4 w-4" /> Aylık SGK
            </Button>
            {isAdmin && (
              <Button data-testid="payroll-params-btn" variant="outline" onClick={() => setParamsOpen(true)} className="border-slate-300">
                <Settings2 className="mr-1.5 h-4 w-4" /> Parametreler
              </Button>
            )}
            <Button data-testid="personnel-advances-btn" variant="outline" onClick={() => setAdvancesOpen(true)} className="border-slate-300">
              <Wallet className="mr-1.5 h-4 w-4" /> Avanslar
            </Button>
            {archived?.count > 0 && (
              <Button data-testid="payroll-archived-btn" variant="outline" onClick={() => { setArchivedOpen(true); loadArchived(); }} className="border-slate-300" title="Silinmiş personele ait arşivlenmiş bordro kayıtlarını görüntüle / geri al">
                <Archive className="mr-1.5 h-4 w-4" /> Arşiv ({archived.count})
              </Button>
            )}
            {pay?.paid_count > 0 && (
              <>
                <Button data-testid="payroll-bulk-pdf-btn" variant="outline" title="Bu dönemin işlenmiş tüm maaş pusulaları — tek PDF"
                  onClick={() => downloadBlob(`/accounting/payroll/payslips/bulk-export?period=${period}&format=pdf`, `maas-pusulalari_${period}.pdf`)} className="border-slate-300">
                  <Receipt className="mr-1.5 h-4 w-4" /> Toplu Pusula
                </Button>
                <Button data-testid="payroll-bulk-zip-btn" variant="outline" title="Her personel ayrı PDF — ZIP arşivi"
                  onClick={() => downloadBlob(`/accounting/payroll/payslips/bulk-export?period=${period}&format=zip`, `maas-pusulalari_${period}.zip`)} className="border-slate-300">
                  <FileDown className="mr-1.5 h-4 w-4" /> ZIP
                </Button>
                <Button data-testid="payroll-bulk-email-btn" variant="outline" disabled={bulkEmailing} title="E-postası olan tüm personele bu dönemin pusulasını gönder"
                  onClick={bulkEmailPayslips} className="border-slate-300">
                  {bulkEmailing ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Mail className="mr-1.5 h-4 w-4" />} Toplu E-posta
                </Button>
              </>
            )}
            {canWrite && (
              <Button data-testid="payroll-run-btn" onClick={() => setRunOpen(true)} disabled={!(pay?.pending_total > 0)}
                className="bg-brand-green text-white hover:bg-brand-green-600">
                <Coins className="mr-1.5 h-4 w-4" /> Bordro Çalıştır
              </Button>
            )}
          </div>
        </div>
        {pay && (
          <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
            <Kpi testid="payroll-total" label={`${pay.period_label} — Brüt`} value={fmtTL(pay.total_gross)} sub={`${pay.count} aktif personel`} Icon={Users} />
            <Kpi testid="payroll-net" label="Net (Ele Geçen)" value={fmtTL(pay.total_take_home)} sub="tüm aktifler" Icon={Wallet} accent="text-emerald-600" />
            <Kpi testid="payroll-deductions" label="Yasal Kesintiler" value={fmtTL(pay.total_deductions)} sub="SGK+GV+DV" Icon={Percent} accent="text-rose-600" />
            <Kpi testid="payroll-monthly" label="İşveren Maliyeti" value={fmtTL(pay.total_employer_cost)} sub="toplam yük" Icon={TrendingUp} accent="text-brand-orange" />
            <Kpi testid="payroll-paid" label="İşlenen" value={fmtTL(pay.paid_total)} sub={`${pay.paid_count} personel`} Icon={Coins} accent="text-emerald-600" />
            <Kpi testid="payroll-pending" label="Bekleyen" value={fmtTL(pay.pending_total)} sub={`${pay.count - pay.paid_count} personel`} Icon={Landmark} accent="text-amber-600" />
          </div>
        )}
      </div>

      <p className="overline">Personel Listesi</p>
      <div className="overflow-x-auto rounded-md bg-white ring-1 ring-inset ring-slate-200">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-left text-xs text-slate-500">
            <tr>
              <SortTh label="Kod" k="code" sortKey={sortKey} dir={sortDir} onSort={onSort} testid="pers-sort-code" />
              <SortTh label="Ad Soyad" k="name" sortKey={sortKey} dir={sortDir} onSort={onSort} testid="pers-sort-name" />
              <SortTh label="Görev" k="title" sortKey={sortKey} dir={sortDir} onSort={onSort} testid="pers-sort-title" />
              <SortTh label="Maaş (Brüt/Net)" k="salary" align="right" sortKey={sortKey} dir={sortDir} onSort={onSort} testid="pers-sort-salary" />
              <th className="px-4 py-3 text-right">İşveren Mal.</th>
              <SortTh label="Ödeme" k="account" sortKey={sortKey} dir={sortDir} onSort={onSort} testid="pers-sort-account" />
              <th className="px-4 py-3">{pay?.period_label || "Dönem"}</th>
              <th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {loading && <tr><td colSpan={8} className="px-4 py-8 text-center text-slate-400">Yükleniyor...</td></tr>}
            {!loading && data.items.length === 0 && <tr><td colSpan={8} className="px-4 py-10 text-center text-slate-400">Personel yok. "Yeni Personel" ile ekleyin; maaşları bordro ile işveren maliyeti olarak deftere işlenir.</td></tr>}
            {sortRows(data.items, cols).slice(pageSize === "all" ? 0 : (page - 1) * pageSize, pageSize === "all" ? undefined : page * pageSize).map((p) => {
              const r = payByPid[p.id];
              return (
              <tr key={p.id} data-testid={`personnel-row-${p.id}`} className={`hover:bg-slate-50 ${p.active === false ? "opacity-50" : ""}`}>
                <td className="px-4 py-3 font-mono text-xs text-slate-400">{p.code}</td>
                <td className="px-4 py-3 font-medium text-slate-700"><div className="flex items-center gap-2.5"><EntityAvatar src={p.avatar_url} name={p.name} size={32} /><span>{p.name}{p.active === false && <span className="ml-1.5 rounded-full bg-slate-100 px-1.5 py-0.5 text-[10px] text-slate-500">Pasif</span>}{p.salary_type === "net" && <span className="ml-1.5 rounded-full bg-sky-100 px-1.5 py-0.5 text-[10px] font-semibold text-sky-700">Net</span>}{p.insured === false && <span className="ml-1.5 rounded-full bg-amber-100 px-1.5 py-0.5 text-[10px] font-semibold text-amber-700">SGK'sız</span>}{p.active !== false && !((p.email || "").includes("@")) && <span data-testid={`pers-no-email-${p.id}`} title="Toplu e-postada atlanır — personel kartına e-posta ekleyin" className="ml-1.5 rounded-full bg-rose-100 px-1.5 py-0.5 text-[10px] font-semibold text-rose-700">E-posta yok</span>}</span></div></td>
                <td className="px-4 py-3 text-slate-500">{p.title || "—"}</td>
                <td className="px-4 py-3 text-right">
                  {(() => {
                    const grossVal = r?.gross ?? (p.salary_type === "net" ? null : p.monthly_salary);
                    const netVal = r?.take_home ?? (p.salary_type === "net" ? p.monthly_salary : null);
                    return (
                      <div className="space-y-0.5">
                        <div className="tabular text-xs" data-testid={`pers-gross-${p.id}`}><span className="mr-1 text-[10px] font-normal text-slate-400">Brüt</span><span className="font-semibold text-slate-700">{grossVal != null ? fmtTL(grossVal) : "—"}</span></div>
                        <div className="tabular text-xs" data-testid={`pers-net-${p.id}`}><span className="mr-1 text-[10px] font-normal text-slate-400">Net</span><span className="font-semibold text-emerald-600">{netVal != null ? fmtTL(netVal) : "—"}</span></div>
                      </div>
                    );
                  })()}
                </td>
                <td className="px-4 py-3 text-right tabular text-xs text-brand-orange">{r ? fmtTL(r.employer_cost) : "—"}</td>
                <td className="px-4 py-3"><div className="flex flex-col gap-1"><span className="text-xs text-slate-500">{p.account === "cash" ? "Nakit" : "Banka"}</span>{p.bank && <BankBadge code={p.bank} />}</div></td>
                <td className="px-4 py-3">
                  {p.active === false ? <span className="text-xs text-slate-300">—</span> :
                    paidMap[p.id] ? <span data-testid={`pers-paid-${p.id}`} className="rounded-full bg-emerald-100 px-2 py-0.5 text-[11px] font-semibold text-emerald-700">İşlendi</span>
                      : <span data-testid={`pers-unpaid-${p.id}`} className="rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-semibold text-amber-700">Bekliyor</span>}
                </td>
                <td className="px-4 py-3 text-right">
                  <div className="flex items-center justify-end gap-2">
                    <button data-testid={`personnel-detail-${p.id}`} onClick={() => { setViewing(p); setViewDocs(p.documents || []); }} title="Detay & Özlük" className="text-slate-300 hover:text-brand-green"><Eye className="h-4 w-4" /></button>
                    <button data-testid={`personnel-payslip-${p.id}`} onClick={() => setPayslip({ pid: p.id, period })} title="Maaş Pusulası" className="text-slate-300 hover:text-brand-green"><Receipt className="h-4 w-4" /></button>
                    {canWrite && <>
                      <button data-testid={`personnel-edit-${p.id}`} onClick={() => setEditing(p)} title="Düzenle" className="text-slate-300 hover:text-brand-green"><FileText className="h-4 w-4" /></button>
                      <button data-testid={`personnel-del-${p.id}`} onClick={() => del(p.id)} title="Sil" className="text-slate-300 hover:text-red-500"><Trash2 className="h-4 w-4" /></button>
                    </>}
                  </div>
                </td>
              </tr>
            );})}
          </tbody>
        </table>
      </div>
      {!loading && data.items.length > 0 && (
        <div className="flex flex-wrap items-center justify-between gap-3 px-1 text-xs text-slate-500" data-testid="personnel-pagination">
          <div className="flex items-center gap-2">
            <span>Sayfa başına:</span>
            {[10, 20, 50, "all"].map((s) => (
              <button key={String(s)} data-testid={`pers-page-size-${s}`} onClick={() => { setPageSize(s); setPage(1); }}
                className={`rounded-md px-2 py-1 font-semibold transition-colors ${pageSize === s ? "bg-brand-green text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}>
                {s === "all" ? "Hepsi" : s}
              </button>
            ))}
            <span className="ml-2 text-slate-400">Toplam {data.items.length} personel</span>
          </div>
          {pageSize !== "all" && Math.ceil(data.items.length / pageSize) > 1 && (
            <div className="flex items-center gap-2">
              <button data-testid="pers-page-prev" disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}
                className="rounded-md border border-slate-200 px-2 py-1 font-semibold transition-colors hover:bg-slate-50 disabled:opacity-40">‹ Önceki</button>
              <span data-testid="pers-page-indicator" className="font-semibold text-slate-600">{page} / {Math.ceil(data.items.length / pageSize)}</span>
              <button data-testid="pers-page-next" disabled={page >= Math.ceil(data.items.length / pageSize)} onClick={() => setPage((p) => Math.min(Math.ceil(data.items.length / pageSize), p + 1))}
                className="rounded-md border border-slate-200 px-2 py-1 font-semibold transition-colors hover:bg-slate-50 disabled:opacity-40">Sonraki ›</button>
            </div>
          )}
        </div>
      )}
      {editing && <PersonnelDialog person={editing} locs={locs} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); loadPay(); }} />}
      {viewing && (
        <EntityDetailDialog open onClose={() => setViewing(null)} testid="personnel-detail-dialog"
          title={viewing.name} avatarSrc={viewing.avatar_url} avatarName={viewing.name}
          subtitle={`${viewing.title || "—"}${viewing.code ? ` · ${viewing.code}` : ""}`}
          sections={[
            { heading: "Kimlik & İletişim", rows: [
              { label: "TC Kimlik No", value: viewing.national_id },
              { label: "Telefon", value: viewing.phone },
              { label: "E-posta", value: viewing.email },
              { label: "Doğum Tarihi", value: viewing.birth_date },
              { label: "Adres", value: viewing.address, full: true },
            ]},
            { heading: "İş Bilgileri", rows: [
              { label: "Görev", value: viewing.title },
              { label: "Departman", value: viewing.department },
              { label: "İşe Giriş", value: viewing.hire_date },
              { label: "SGK Sicil No", value: viewing.sgk_no },
              { label: "Durum", value: viewing.active === false ? "Pasif" : "Aktif", accent: viewing.active === false ? "text-slate-400" : "text-emerald-600" },
            ]},
            { heading: "Maaş & Banka", rows: [
              { label: "Maaş", value: viewing.monthly_salary != null ? fmtTL(viewing.monthly_salary) : "—" },
              { label: "Maaş Tipi", value: viewing.salary_type === "net" ? "Net" : "Brüt" },
              { label: "SGK", value: viewing.insured === false ? "SGK'sız" : "SGK'lı" },
              { label: "Ödeme", value: viewing.account === "cash" ? "Nakit" : "Banka" },
              { label: "Banka", value: viewing.bank },
              { label: "IBAN", value: viewing.iban, full: true },
            ]},
            { heading: "Acil Durum", rows: [
              { label: "İletişim Kişisi", value: viewing.emergency_contact },
              { label: "İletişim Telefon", value: viewing.emergency_phone },
            ]},
          ]}
          docs={viewDocs}
          docConfig={{ canWrite, testid: "personnel-docs", zipPath: `/accounting/personnel/${viewing.id}/documents/zip`,
            onAdd: async (body) => { const { data } = await api.post(`/accounting/personnel/${viewing.id}/documents`, body); setViewDocs((d) => [...d, data]); toast.success("Evrak eklendi"); },
            onDelete: async (id) => { await api.delete(`/accounting/personnel/${viewing.id}/documents/${id}`); setViewDocs((d) => d.filter((x) => x.id !== id)); toast.success("Evrak silindi"); } }}
          docHeading="Özlük Dosyası (Kimlik / Sözleşme / SGK / Diğer)" />
      )}
      {payslip && <PayslipDialog pid={payslip.pid} period={payslip.period} onClose={() => setPayslip(null)} />}
      {runOpen && pay && <PayrollRunDialog period={period} periodLabel={pay.period_label} pending={(pay.items || []).filter((i) => !i.paid)} onClose={() => setRunOpen(false)} onDone={() => { setRunOpen(false); loadPay(); }} />}
      {paramsOpen && <PayrollParamsDialog onClose={() => setParamsOpen(false)} onSaved={() => { setParamsOpen(false); loadPay(); }} />}
      {annualOpen && <PayrollAnnualDialog onClose={() => setAnnualOpen(false)} />}
      {advancesOpen && <AdvancesDialog isAdmin={isAdmin} onClose={() => setAdvancesOpen(false)} />}
      {bulkResult && <BulkEmailReportDialog result={bulkResult} onClose={() => setBulkResult(null)} onArchiveOrphans={archiveOrphans} archiving={archiving} />}
      {archivedOpen && <ArchivedOrphansDialog data={archived} onRestore={restoreOrphans} onClose={() => setArchivedOpen(false)} />}
    </div>
  );
}

// ─── Toplu E-posta Sonuç Raporu ────────────────────────────────────────────
const ArchivedOrphansDialog = ({ data, onRestore, onClose }) => {
  const items = data?.items || [];
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent data-testid="archived-orphans-dialog" className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Archive className="h-5 w-5 text-slate-600" /> Arşivlenmiş Bordro Kayıtları</DialogTitle>
          <DialogDescription>Silinmiş personele ait, listelerden gizlenmiş {data?.count || 0} kayıt. Muhasebe defterinden silinmez; geri alırsanız tekrar listelerde/pusulalarda görünür.</DialogDescription>
        </DialogHeader>
        {items.length === 0 ? (
          <p className="py-6 text-center text-sm text-slate-500" data-testid="archived-empty">Arşivlenmiş kayıt yok.</p>
        ) : (
          <>
            <div className="mb-2 flex justify-end">
              <Button data-testid="archived-restore-all" size="sm" variant="outline" onClick={() => onRestore()} className="border-slate-300">
                <RotateCcw className="mr-1.5 h-4 w-4" /> Tümünü Geri Al
              </Button>
            </div>
            <ul className="max-h-72 divide-y divide-slate-100 overflow-y-auto rounded-md border border-slate-200">
              {items.map((it, i) => (
                <li key={i} data-testid="archived-orphan-row" className="flex items-center justify-between px-3 py-2">
                  <div>
                    <div className="text-sm font-medium text-slate-700">{it.name}</div>
                    <div className="text-[11px] text-slate-400">{it.records} kayıt · {(it.periods || []).join(", ")}</div>
                  </div>
                  <Button data-testid={`archived-restore-${it.personnel_id}`} size="sm" variant="ghost" onClick={() => onRestore(it.personnel_id)} className="text-brand-green hover:bg-emerald-50">
                    <RotateCcw className="mr-1 h-3.5 w-3.5" /> Geri Al
                  </Button>
                </li>
              ))}
            </ul>
          </>
        )}
        <div className="mt-2 flex justify-end">
          <Button data-testid="archived-close" variant="outline" onClick={onClose}>Kapat</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const BulkEmailReportDialog = ({ result, onClose, onArchiveOrphans, archiving }) => {
  const sent = result.sent || [], skipped = result.skipped || [], failed = result.failed || [];
  const Section = ({ title, rows, tone, testid, showReason }) => (
    <div data-testid={testid} className="rounded-md border border-slate-200">
      <div className={`flex items-center justify-between px-3 py-2 text-xs font-semibold ${tone}`}>
        <span>{title}</span><span className="tabular">{rows.length}</span>
      </div>
      {rows.length > 0 && (
        <ul className="max-h-40 divide-y divide-slate-100 overflow-y-auto text-sm">
          {rows.map((r, i) => (
            <li key={i} className="flex items-center justify-between px-3 py-1.5">
              <span className="text-slate-700">{r.name || "Silinmiş personel"}</span>
              <span className="text-[11px] text-slate-400">{showReason ? (r.reason || "") : (r.to || "")}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent data-testid="bulk-email-report-dialog" className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Mail className="h-5 w-5 text-brand-green" /> Toplu E-posta Sonucu</DialogTitle>
          <DialogDescription>{result.period_label} — {sent.length} gönderildi · {skipped.length} atlandı · {failed.length} başarısız</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-3 gap-2">
          <Kpi testid="bulk-email-sent-kpi" label="Gönderildi" value={String(sent.length)} sub="e-posta" Icon={Mail} accent="text-emerald-600" />
          <Kpi testid="bulk-email-skipped-kpi" label="Atlandı" value={String(skipped.length)} sub="e-posta yok" Icon={Users} accent="text-amber-600" />
          <Kpi testid="bulk-email-failed-kpi" label="Başarısız" value={String(failed.length)} sub="hata" Icon={Users} accent="text-rose-600" />
        </div>
        <div className="mt-2 space-y-2">
          <Section title="Gönderilenler" rows={sent} tone="bg-emerald-50 text-emerald-700" testid="bulk-email-sent-list" showReason={false} />
          {skipped.length > 0 && <Section title="Atlananlar (e-posta yok)" rows={skipped} tone="bg-amber-50 text-amber-700" testid="bulk-email-skipped-list" showReason />}
          {failed.length > 0 && <Section title="Başarısız" rows={failed} tone="bg-rose-50 text-rose-700" testid="bulk-email-failed-list" showReason />}
        </div>
        {skipped.length > 0 && (
          <p className="mt-1 rounded-md bg-amber-50 px-3 py-2 text-[11px] text-amber-700">
            Atlanan personelin kartına geçerli bir e-posta ekleyip toplu göndermeyi tekrar çalıştırın. Listede kırmızı <b>E-posta yok</b> rozetiyle işaretlidirler.
          </p>
        )}
        <div className="mt-2 flex items-center justify-between gap-2">
          {failed.some((f) => f.deleted) && onArchiveOrphans ? (
            <Button data-testid="bulk-email-archive-orphans" size="sm" variant="outline" disabled={archiving} onClick={onArchiveOrphans} className="border-amber-300 text-amber-700 hover:bg-amber-50">
              <Archive className="mr-1.5 h-4 w-4" /> Silinmiş personel kayıtlarını arşivle
            </Button>
          ) : <span />}
          <Button data-testid="bulk-email-report-close" variant="outline" onClick={onClose}>Kapat</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// ─── Maaş Pusulası (bordro dökümü) ─────────────────────────────────────────
const PLine = ({ label, value, sub, kind }) => (
  <div className="flex items-center justify-between py-1 text-sm" data-testid={`payslip-line-${(label || "").toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
    <span className={`${kind === "total" ? "font-semibold text-slate-800" : "text-slate-500"}`}>{label}{sub && <span className="ml-1 text-[11px] text-slate-400">{sub}</span>}</span>
    <span className={`tabular ${kind === "deduct" ? "text-rose-600" : kind === "total" ? "text-lg font-bold text-slate-900" : kind === "net" ? "font-bold text-emerald-700" : "font-medium text-slate-700"}`}>{kind === "deduct" ? "−" : ""}{fmtTL(value)}</span>
  </div>
);

function PayslipDialog({ pid, period, onClose }) {
  const [d, setD] = useState(null);
  const [err, setErr] = useState(false);
  const [emailing, setEmailing] = useState(false);
  const emailIt = async () => {
    setEmailing(true);
    try {
      const { data: r } = await api.post(`/accounting/payroll/payslip/${pid}/email`, null, { params: { period } });
      toast.success(`Pusula ${r.to} adresine gönderildi`);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); } finally { setEmailing(false); }
  };
  useEffect(() => {
    api.get(`/accounting/payroll/payslip/${pid}`, { params: { period } })
      .then((r) => setD(r.data)).catch(() => setErr(true));
  }, [pid, period]);
  const b = d?.bordro || {};
  const legalDeductions = (b.sgk_employee || 0) + (b.unemployment_employee || 0) + (b.income_tax || 0) + (b.stamp_tax || 0);
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="payslip-dialog" className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2"><Receipt className="h-5 w-5 text-brand-green" /> Maaş Pusulası</DialogTitle>
          <DialogDescription>{d ? `${d.personnel?.name} · ${d.period_label}${d.paid ? " · İşlendi" : " · Önizleme"}` : "Yükleniyor…"}</DialogDescription>
        </DialogHeader>
        {err && <p className="py-6 text-center text-sm text-rose-600">Pusula yüklenemedi.</p>}
        {!err && !d && <p className="py-6 text-center text-sm text-slate-400"><Loader2 className="mr-2 inline h-4 w-4 animate-spin" />Hesaplanıyor…</p>}
        {d && (
          <div className="max-h-[70vh] space-y-3 overflow-y-auto pr-1">
            <div className="rounded-md bg-slate-50 p-3 text-xs text-slate-500">
              <div className="flex justify-between"><span>{d.personnel?.title || "—"}{d.personnel?.national_id ? ` · TC ${d.personnel.national_id}` : ""}</span><span>Çalışma günü: {b.work_days || 30}</span></div>
              <div className="flex justify-between"><span>{b.insured === false ? "SGK'sız (stopajsız)" : "SGK'lı"} · {b.salary_type === "net" ? "Net anlaşma" : "Brüt anlaşma"}</span><span>{d.params?.year} parametreleri</span></div>
            </div>

            <div className="rounded-md border border-slate-200 p-3">
              <p className="mb-1 text-[11px] font-bold uppercase tracking-wide text-slate-400">Kazançlar</p>
              <PLine label="Temel Brüt" value={b.base_amount} />
              {b.bonus > 0 && <PLine label="Prim / Ek Kazanç" value={b.bonus} />}
              <div className="border-t border-slate-100 pt-1"><PLine label="Brüt Toplam" value={b.gross} kind="total" /></div>
            </div>

            {b.insured !== false && (
              <div className="rounded-md border border-slate-200 p-3">
                <p className="mb-1 text-[11px] font-bold uppercase tracking-wide text-slate-400">Yasal Kesintiler</p>
                <PLine label="SGK İşçi Payı %14" value={b.sgk_employee} kind="deduct" sub={`matrah ${fmtTL(b.sgk_base)}`} />
                <PLine label="İşsizlik İşçi %1" value={b.unemployment_employee} kind="deduct" />
                <PLine label="Gelir Vergisi (hesaplanan)" value={b.income_tax_gross} kind="deduct" sub={`matrah ${fmtTL(b.income_tax_base)}`} />
                <PLine label="Asgari Ücret GV İstisnası" value={b.income_tax_exemption} />
                <PLine label="Ödenecek Gelir Vergisi" value={b.income_tax} kind="deduct" />
                <PLine label="Damga Vergisi ‰7,59" value={b.stamp_tax_gross} kind="deduct" />
                <PLine label="Damga Vergisi İstisnası" value={b.stamp_tax_exemption} />
                <PLine label="Ödenecek Damga Vergisi" value={b.stamp_tax} kind="deduct" />
                <div className="border-t border-slate-100 pt-1"><PLine label="Yasal Kesinti Toplamı" value={Math.round(legalDeductions * 100) / 100} kind="total" /></div>
              </div>
            )}

            <div className="rounded-md border border-emerald-200 bg-emerald-50/40 p-3">
              <PLine label="Net Maaş" value={b.net_salary} kind="net" />
              {b.transport_total > 0 && <PLine label="+ Yol Yardımı" value={b.transport_total} />}
              {b.meal_total > 0 && <PLine label={`+ Yemek ${b.meal_as_card ? "(kart)" : "(nakit)"}`} value={b.meal_total} sub={b.meal_as_card ? "ele geçene eklenmez" : ""} />}
              {b.advance > 0 && <PLine label="− Avans / Kesinti" value={b.advance} kind="deduct" />}
              <div className="mt-1 border-t border-emerald-200 pt-1"><PLine label="ELE GEÇEN (Net Ödenen)" value={b.take_home} kind="total" /></div>
            </div>

            <div className="rounded-md border border-orange-200 bg-orange-50/40 p-3">
              <p className="mb-1 text-[11px] font-bold uppercase tracking-wide text-slate-400">İşveren Maliyeti</p>
              <PLine label="SGK İşveren Payı" value={b.sgk_employer} />
              <PLine label="İşsizlik İşveren" value={b.unemployment_employer} />
              <div className="border-t border-orange-200 pt-1"><PLine label="Toplam İşveren Maliyeti" value={b.employer_cost} kind="total" /></div>
            </div>

            {d.entitlements?.has && (
              <div className="rounded-md border border-sky-200 bg-sky-50/40 p-3" data-testid="payslip-entitlements">
                <p className="mb-1 text-[11px] font-bold uppercase tracking-wide text-slate-400">Kıdem, İhbar & Yıllık İzin (tahmini)</p>
                <div className="flex items-center justify-between py-1 text-sm"><span className="text-slate-500">Kıdem süresi</span><span className="font-medium text-slate-700">{d.entitlements.seniority_years} yıl {d.entitlements.seniority_months} ay</span></div>
                <div className="flex items-center justify-between py-1 text-sm"><span className="text-slate-500">Yıllık izin hakkı</span><span className="font-medium text-slate-700">{d.entitlements.annual_leave_days} gün</span></div>
                <PLine label="Kıdem Tazminatı (tahmini)" value={d.entitlements.severance_amount} />
                <PLine label={`İhbar Tazminatı (${d.entitlements.notice_weeks} hafta)`} value={d.entitlements.notice_amount} />
                {d.entitlements.severance_capped && <p className="mt-1 text-[10px] text-amber-600">Kıdem tazminatı tavanı ({fmtTL(d.entitlements.severance_ceiling)}) uygulandı.</p>}
              </div>
            )}
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
              <Button data-testid="payslip-pdf" variant="outline" onClick={() => downloadBlob(`/accounting/payroll/payslip/${pid}/export?period=${period}&format=pdf`, `maas-pusulasi_${period}.pdf`)}><FileDown className="mr-1.5 h-4 w-4" /> PDF</Button>
              <Button data-testid="payslip-xlsx" variant="outline" onClick={() => downloadBlob(`/accounting/payroll/payslip/${pid}/export?period=${period}&format=xlsx`, `maas-pusulasi_${period}.xlsx`)}><FileSpreadsheet className="mr-1.5 h-4 w-4" /> Excel</Button>
              <Button data-testid="payslip-email" variant="outline" onClick={emailIt} disabled={emailing}><Mail className="mr-1.5 h-4 w-4" /> {emailing ? "Gönderiliyor…" : "E-posta"}</Button>
              <Button data-testid="payslip-print" variant="outline" onClick={() => window.print()}><Printer className="mr-1.5 h-4 w-4" /> Yazdır</Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

// ─── Personel Avans Özeti (verilen / mahsup / açık + limit) ────────────────
function AdvancesDialog({ isAdmin, onClose }) {
  const [d, setD] = useState(null);
  const [err, setErr] = useState(false);
  const [pct, setPct] = useState("");
  const [enabled, setEnabled] = useState(true);
  const [saving, setSaving] = useState(false);
  const load = () => api.get("/accounting/personnel/advances").then((r) => {
    setD(r.data); setPct(String(r.data.limit_pct ?? 50)); setEnabled(r.data.limit_enabled !== false);
  }).catch(() => setErr(true));
  useEffect(() => { load(); }, []);
  const saveLimit = async () => {
    setSaving(true);
    try {
      await api.put("/accounting/advance-limit", { enabled, pct: parseFloat(String(pct).replace(",", ".")) || 0 });
      toast.success("Avans limiti güncellendi");
      load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); } finally { setSaving(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="advances-dialog" className="max-w-3xl">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2"><Wallet className="h-5 w-5 text-brand-green" /> Personel Avansları</DialogTitle>
          <DialogDescription>Verilen, maaştan mahsup edilen ve kalan (açık) avanslar. Açık avansı limitini aşan personel kırmızı işaretlenir.</DialogDescription>
        </DialogHeader>
        {err && <p className="py-6 text-center text-sm text-rose-600">Yüklenemedi.</p>}
        {!err && !d && <p className="py-6 text-center text-sm text-slate-400"><Loader2 className="mr-2 inline h-4 w-4 animate-spin" />Yükleniyor…</p>}
        {d && (
          <div className="max-h-[70vh] space-y-3 overflow-y-auto pr-1">
            <div className="grid grid-cols-3 gap-2">
              <div className="rounded-md bg-slate-50 p-2 text-center"><p className="text-[11px] text-slate-500">Verilen</p><p className="tabular text-base font-bold text-slate-800" data-testid="advances-total-given">{fmtTL(d.total_given)}</p></div>
              <div className="rounded-md bg-emerald-50 p-2 text-center"><p className="text-[11px] text-emerald-600">Mahsup</p><p className="tabular text-base font-bold text-emerald-700">{fmtTL(d.total_settled)}</p></div>
              <div className="rounded-md bg-amber-50 p-2 text-center"><p className="text-[11px] text-amber-600">Açık (kalan)</p><p className="tabular text-base font-bold text-amber-700" data-testid="advances-total-open">{fmtTL(d.total_open)}</p></div>
            </div>
            {isAdmin && (
              <div className="flex flex-wrap items-center gap-2 rounded-md border border-slate-200 p-2 text-sm">
                <label className="flex items-center gap-1.5 text-slate-600"><input type="checkbox" data-testid="advance-limit-enabled" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} className="h-3.5 w-3.5 accent-brand-green" /> Limit uyarısı</label>
                <span className="text-slate-500">Maaşın</span>
                <Input data-testid="advance-limit-pct" value={pct} onChange={(e) => setPct(e.target.value)} inputMode="decimal" className="h-8 w-16 text-right tabular" />
                <span className="text-slate-500">%'sini aşınca uyar</span>
                <Button data-testid="advance-limit-save" size="sm" onClick={saveLimit} disabled={saving} className="ml-auto bg-brand-green text-white hover:bg-brand-green-600">{saving ? "…" : "Kaydet"}</Button>
              </div>
            )}
            {d.items.length === 0 ? (
              <p className="py-6 text-center text-sm text-slate-400">Kayıtlı personel avansı yok.</p>
            ) : (
              <table className="w-full text-sm" data-testid="advances-table">
                <thead><tr className="border-b text-left text-[11px] uppercase text-slate-400">
                  <th className="px-2 py-1.5">Personel</th><th className="px-2 py-1.5 text-right">Verilen</th>
                  <th className="px-2 py-1.5 text-right">Mahsup</th><th className="px-2 py-1.5 text-right">Açık</th>
                  <th className="px-2 py-1.5 text-right">Limit</th><th className="px-2 py-1.5 text-center">Durum</th>
                </tr></thead>
                <tbody>
                  {d.items.map((r) => (
                    <tr key={r.personnel_id} data-testid={`advance-row-${r.personnel_id}`} className="border-b border-slate-50">
                      <td className="px-2 py-1.5"><p className="font-medium text-slate-800">{r.name}</p>{r.title && <p className="text-[11px] text-slate-400">{r.title}</p>}</td>
                      <td className="px-2 py-1.5 text-right tabular text-slate-600">{fmtTL(r.given)}</td>
                      <td className="px-2 py-1.5 text-right tabular text-emerald-600">{fmtTL(r.settled)}</td>
                      <td className="px-2 py-1.5 text-right tabular font-semibold text-amber-700">{fmtTL(r.open)}</td>
                      <td className="px-2 py-1.5 text-right tabular text-slate-400">{r.limit ? fmtTL(r.limit) : "—"}</td>
                      <td className="px-2 py-1.5 text-center">{r.over_limit ? <span className="rounded-full bg-rose-100 px-2 py-0.5 text-[10px] font-bold text-rose-700">LİMİT AŞILDI</span> : r.open > 0 ? <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-medium text-amber-700">açık</span> : <span className="text-[10px] text-slate-300">—</span>}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

// ─── Bordro Çalıştır (avans/prim ile) ──────────────────────────────────────
function PayrollRunDialog({ period, periodLabel, pending, onClose, onDone }) {
  const [ov, setOv] = useState({});   // {pid: {advance, bonus}}
  const [busy, setBusy] = useState(false);
  const setField = (pid, k, v) => setOv((o) => ({ ...o, [pid]: { ...(o[pid] || {}), [k]: v } }));
  const run = async () => {
    setBusy(true);
    try {
      const overrides = {};
      Object.entries(ov).forEach(([pid, val]) => {
        const adv = parseFloat(String(val.advance || "").replace(",", ".")) || 0;
        const bon = parseFloat(String(val.bonus || "").replace(",", ".")) || 0;
        if (adv > 0 || bon > 0) overrides[pid] = { advance: adv, bonus: bon };
      });
      const { data: r } = await api.post("/accounting/payroll/run", { period, overrides });
      if (r.created > 0) toast.success(`${r.period_label}: ${r.created} personel · net ${fmtTL(r.total_net)} · işveren maliyeti ${fmtTL(r.total)} deftere işlendi`);
      else toast.info(`${r.period_label}: yeni işlenecek maaş yok (${r.skipped} atlandı)`);
      onDone();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="payroll-run-dialog" className="max-w-3xl">
        <DialogHeader>
          <DialogTitle className="font-heading">Bordro Çalıştır — {periodLabel}</DialogTitle>
          <DialogDescription>Bekleyen {pending.length} personel için brütten-nete bordro deftere <b>işveren maliyeti</b> olarak işlenir. İsteğe bağlı avans (net'ten düşülür) ve prim (brüte eklenir) girin.</DialogDescription>
        </DialogHeader>
        <div className="max-h-[60vh] overflow-y-auto">
          <table className="w-full text-sm">
            <thead className="sticky top-0 bg-slate-50 text-left text-xs text-slate-500">
              <tr><th className="px-3 py-2">Personel</th><th className="px-3 py-2 text-right">Brüt</th><th className="px-3 py-2 text-right">Net</th><th className="px-3 py-2 text-right">İşv. Mal.</th><th className="px-3 py-2 text-right">Avans {curSym()}</th><th className="px-3 py-2 text-right">Prim {curSym()}</th></tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {pending.length === 0 && <tr><td colSpan={6} className="px-3 py-6 text-center text-slate-400">Bekleyen bordro yok — tümü işlenmiş.</td></tr>}
              {pending.map((p) => (
                <tr key={p.personnel_id} data-testid={`run-row-${p.personnel_id}`}>
                  <td className="px-3 py-2 font-medium text-slate-700">{p.name}<span className="ml-1 text-[11px] text-slate-400">{p.title || ""}</span></td>
                  <td className="px-3 py-2 text-right tabular text-slate-600">{fmtTL(p.gross)}</td>
                  <td className="px-3 py-2 text-right tabular text-emerald-600">{fmtTL(p.take_home)}</td>
                  <td className="px-3 py-2 text-right tabular text-brand-orange">{fmtTL(p.employer_cost)}</td>
                  <td className="px-3 py-2 text-right"><Input data-testid={`run-advance-${p.personnel_id}`} inputMode="decimal" value={(ov[p.personnel_id]?.advance) || ""} onChange={(e) => setField(p.personnel_id, "advance", e.target.value)} placeholder="0" className="h-8 w-24 text-right tabular" />{p.advance_recorded > 0 && <span data-testid={`run-advance-recorded-${p.personnel_id}`} className="mt-0.5 block text-[10px] font-medium text-amber-600">+{fmtTL(p.advance_recorded)} kayıtlı avans</span>}</td>
                  <td className="px-3 py-2 text-right"><Input data-testid={`run-bonus-${p.personnel_id}`} inputMode="decimal" value={(ov[p.personnel_id]?.bonus) || ""} onChange={(e) => setField(p.personnel_id, "bonus", e.target.value)} placeholder="0" className="h-8 w-24 text-right tabular" /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="flex justify-end gap-2 border-t border-slate-100 pt-3">
          <Button variant="ghost" onClick={onClose} disabled={busy}>Vazgeç</Button>
          <Button data-testid="payroll-run-confirm" onClick={run} disabled={busy || pending.length === 0} className="bg-brand-green text-white hover:bg-brand-green-600">
            {busy ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Coins className="mr-1.5 h-4 w-4" />} {pending.length} Personeli İşle
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ─── 2026 Parametre Editörü (Yönetici) ─────────────────────────────────────
const RATE_KEYS = ["sgk_employee_rate", "unemployment_employee_rate", "sgk_employer_rate", "sgk_employer_rate_discounted", "unemployment_employer_rate", "stamp_tax_rate"];
const PARAM_LABELS = {
  year: "Yıl", gross_min_wage_monthly: "Brüt Asgari Ücret (ay)", net_min_wage_monthly: "Net Asgari Ücret (ay)",
  gross_min_wage_daily: "Brüt Asgari Ücret (gün)", sgk_employee_rate: "SGK İşçi %", unemployment_employee_rate: "İşsizlik İşçi %",
  sgk_employer_rate: "SGK İşveren %", sgk_employer_rate_discounted: "SGK İşveren (5 puan indirimli) %", unemployment_employer_rate: "İşsizlik İşveren %",
  sgk_floor_monthly: "SGK Taban (ay)", sgk_ceiling_monthly: "SGK Tavan (ay)", stamp_tax_rate: "Damga Vergisi ‰ (oran)",
  meal_exempt_daily: "Yemek İstisnası (gün)", child_aid_monthly: "Çocuk Yardımı İstisnası (ay)", family_aid_monthly: "Aile Yardımı İstisnası (ay)",
  default_work_days: "Varsayılan Çalışma Günü", severance_ceiling: "Kıdem Tazminatı Tavanı (yıllık brüt)",
};
const EDIT_ORDER = ["year", "gross_min_wage_monthly", "net_min_wage_monthly", "sgk_employee_rate", "unemployment_employee_rate", "sgk_employer_rate", "sgk_employer_rate_discounted", "unemployment_employer_rate", "sgk_floor_monthly", "sgk_ceiling_monthly", "stamp_tax_rate", "meal_exempt_daily", "child_aid_monthly", "family_aid_monthly", "default_work_days", "severance_ceiling"];
const PARAM_GROUPS = [
  { key: "wage", label: "Ücret & Asgari", fields: ["year", "gross_min_wage_monthly", "net_min_wage_monthly", "default_work_days"] },
  { key: "sgk", label: "SGK & İşsizlik", fields: ["sgk_employee_rate", "unemployment_employee_rate", "sgk_employer_rate", "sgk_employer_rate_discounted", "unemployment_employer_rate", "sgk_floor_monthly", "sgk_ceiling_monthly"] },
  { key: "tax", label: "Vergi & İstisna", fields: ["stamp_tax_rate", "meal_exempt_daily", "child_aid_monthly", "family_aid_monthly", "severance_ceiling"] },
];

function PayrollParamsDialog({ onClose, onSaved }) {
  const [meta, setMeta] = useState(null);
  const [form, setForm] = useState({});
  const [brackets, setBrackets] = useState([]);
  const [busy, setBusy] = useState(false);
  const [ptab, setPtab] = useState("wage");
  useEffect(() => {
    api.get("/accounting/payroll/params").then((r) => {
      setMeta(r.data);
      const p = r.data.params || {};
      const f = {};
      EDIT_ORDER.forEach((k) => {
        f[k] = RATE_KEYS.includes(k) ? String(Math.round((p[k] || 0) * 100 * 1000) / 1000) : String(p[k] ?? "");
      });
      setForm(f);
      setBrackets((p.income_tax_brackets || []).map(([lim, rate]) => ({ lim: lim == null ? "" : String(lim), rate: String(Math.round(rate * 100 * 1000) / 1000) })));
    }).catch(() => toast.error("Parametreler yüklenemedi"));
  }, []);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setBr = (i, k, v) => setBrackets((b) => b.map((x, idx) => (idx === i ? { ...x, [k]: v } : x)));
  const num = (v) => parseFloat(String(v).replace(",", ".")) || 0;
  const save = async () => {
    setBusy(true);
    try {
      const params = {};
      EDIT_ORDER.forEach((k) => {
        if (form[k] === "" || form[k] == null) return;
        const raw = num(form[k]);
        params[k] = RATE_KEYS.includes(k) ? raw / 100 : (k === "year" || k === "default_work_days" ? Math.round(raw) : raw);
      });
      params.income_tax_brackets = brackets.map((b) => [b.lim === "" ? null : num(b.lim), num(b.rate) / 100]);
      await api.put("/accounting/payroll/params", { params });
      toast.success("Bordro parametreleri güncellendi");
      onSaved();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  const fillDefaults = () => {
    const p = meta?.defaults || {};
    const f = {};
    EDIT_ORDER.forEach((k) => {
      if (k === "year") { f[k] = String(meta?.current_year || p.year || ""); return; }
      f[k] = RATE_KEYS.includes(k) ? String(Math.round((p[k] || 0) * 100 * 1000) / 1000) : String(p[k] ?? "");
    });
    setForm(f);
    setBrackets((p.income_tax_brackets || []).map(([lim, rate]) => ({ lim: lim == null ? "" : String(lim), rate: String(Math.round(rate * 100 * 1000) / 1000) })));
    toast.info(`Güncel yıl (${meta?.current_year}) varsayılanları forma dolduruldu — kontrol edip Kaydet'e basın`);
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="payroll-params-dialog" className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="font-heading">Bordro Parametreleri</DialogTitle>
          <DialogDescription>Resmî değerler değişince (ör. yıl 2027'ye geçince) buradan güncelleyin. Yürürlükteki yıl: <b>{meta?.params?.year}</b>{meta?.is_overridden ? " (özelleştirilmiş)" : " (fabrika)"} · Sistem yılı: {meta?.current_year}. Boş bırakılan alan varsayılanı korur.</DialogDescription>
        </DialogHeader>
        {!meta && <p className="py-6 text-center text-sm text-slate-400"><Loader2 className="mr-2 inline h-4 w-4 animate-spin" />Yükleniyor…</p>}
        {meta && (
          <div className="max-h-[68vh] space-y-4 overflow-y-auto pr-1">
            <div className="inline-flex flex-wrap gap-1 rounded-lg bg-slate-100 p-1" data-testid="param-tabs">
              {PARAM_GROUPS.map((g) => (
                <button key={g.key} type="button" data-testid={`param-tab-${g.key}`} onClick={() => setPtab(g.key)}
                  className={`rounded-md px-3.5 py-1.5 text-sm font-semibold transition-colors ${ptab === g.key ? "bg-white text-brand-green shadow-sm" : "text-slate-500 hover:text-slate-700"}`}>
                  {g.label}
                </button>
              ))}
            </div>
            <div className="grid grid-cols-2 gap-3">
              {(PARAM_GROUPS.find((g) => g.key === ptab)?.fields || []).map((k) => (
                <div key={k}>
                  <label className="mb-1 block text-xs text-slate-500">{PARAM_LABELS[k]}{RATE_KEYS.includes(k) && " (%)"}</label>
                  <Input data-testid={`param-${k}`} inputMode="decimal" value={form[k] ?? ""} onChange={(e) => set(k, e.target.value)} className="tabular h-9" />
                </div>
              ))}
            </div>
            {ptab === "tax" && (
              <div data-testid="param-brackets">
                <p className="mb-2 text-[11px] font-bold uppercase tracking-wide text-slate-400">Gelir Vergisi Dilimleri (kümülatif matrah üst sınırı · oran %)</p>
                <div className="space-y-2">
                  {brackets.map((b, i) => (
                    <div key={i} className="flex items-center gap-2" data-testid={`param-bracket-${i}`}>
                      <Input inputMode="decimal" value={b.lim} onChange={(e) => setBr(i, "lim", e.target.value)} placeholder={i === brackets.length - 1 ? "∞ (son dilim, boş)" : `üst sınır ${curSym()}`} className="tabular h-9 flex-1" />
                      <Input inputMode="decimal" value={b.rate} onChange={(e) => setBr(i, "rate", e.target.value)} placeholder="oran %" className="tabular h-9 w-24" />
                      <span className="text-slate-400">%</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
            <div className="flex items-center justify-between gap-2 border-t border-slate-100 pt-3">
              <Button data-testid="param-reset" variant="ghost" onClick={fillDefaults} disabled={busy} className="text-slate-500"><RotateCcw className="mr-1.5 h-4 w-4" /> Güncel Yıl Verilerini Getir</Button>
              <div className="flex gap-2">
                <Button variant="outline" onClick={onClose} disabled={busy}>Vazgeç</Button>
                <Button data-testid="param-save" onClick={save} disabled={busy} className="bg-brand-green text-white hover:bg-brand-green-600">{busy ? "Kaydediliyor…" : "Kaydet"}</Button>
              </div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function PayrollAnnualDialog({ onClose }) {
  const nowY = new Date().getFullYear();
  const [year, setYear] = useState(nowY);
  const [d, setD] = useState(null);
  const load = useCallback(() => {
    setD(null);
    api.get("/accounting/payroll/annual-summary", { params: { year } }).then((r) => setD(r.data)).catch(() => toast.error("İcmal yüklenemedi"));
  }, [year]);
  useEffect(() => { load(); }, [load]);
  const t = d?.totals;
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="payroll-annual-dialog" className="max-w-4xl">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2"><FileBarChart className="h-5 w-5 text-brand-green" /> Yıllık Bordro İcmali (SGK & Vergi)</DialogTitle>
          <DialogDescription>Mali müşavir için: işlenmiş bordroların personel bazında yıllık toplamı. Yıl seçin, Excel/PDF olarak dışa aktarın.</DialogDescription>
        </DialogHeader>
        <div className="flex flex-wrap items-center justify-between gap-2">
          <select data-testid="annual-year" value={year} onChange={(e) => setYear(parseInt(e.target.value, 10))} className="h-9 rounded-md border border-input bg-white px-2 text-sm">
            {Array.from({ length: 6 }, (_, i) => nowY + 1 - i).map((y) => <option key={y} value={y}>{y}</option>)}
          </select>
          <div className="flex gap-2">
            <Button data-testid="annual-xlsx" variant="outline" onClick={() => downloadBlob(`/accounting/payroll/annual-summary/export?year=${year}&format=xlsx`, `bordro-yillik-icmal_${year}.xlsx`)}><FileSpreadsheet className="mr-1.5 h-4 w-4" /> Excel</Button>
            <Button data-testid="annual-pdf" variant="outline" onClick={() => downloadBlob(`/accounting/payroll/annual-summary/export?year=${year}&format=pdf`, `bordro-yillik-icmal_${year}.pdf`)}><FileDown className="mr-1.5 h-4 w-4" /> PDF</Button>
          </div>
        </div>
        <div className="max-h-[58vh] overflow-auto rounded-md ring-1 ring-inset ring-slate-200">
          <table className="w-full text-sm">
            <thead className="sticky top-0 bg-slate-50 text-left text-xs text-slate-500">
              <tr><th className="px-3 py-2">Personel</th><th className="px-3 py-2 text-right">Ay</th><th className="px-3 py-2 text-right">Brüt</th><th className="px-3 py-2 text-right">SGK İşçi</th><th className="px-3 py-2 text-right">Vergi</th><th className="px-3 py-2 text-right">Net</th><th className="px-3 py-2 text-right">Ele Geçen</th><th className="px-3 py-2 text-right">İşveren Mal.</th></tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {!d && <tr><td colSpan={8} className="px-3 py-8 text-center text-slate-400"><Loader2 className="mr-2 inline h-4 w-4 animate-spin" />Yükleniyor…</td></tr>}
              {d && d.rows.length === 0 && <tr><td colSpan={8} className="px-3 py-8 text-center text-slate-400">{year} için işlenmiş bordro yok.</td></tr>}
              {d && d.rows.map((r) => (
                <tr key={r.personnel_id} data-testid={`annual-row-${r.personnel_id}`}>
                  <td className="px-3 py-2 font-medium text-slate-700">{r.name}</td>
                  <td className="px-3 py-2 text-right tabular text-slate-500">{r.months}</td>
                  <td className="px-3 py-2 text-right tabular">{fmtTL(r.gross)}</td>
                  <td className="px-3 py-2 text-right tabular text-rose-600">{fmtTL(r.sgk_employee + r.unemployment_employee)}</td>
                  <td className="px-3 py-2 text-right tabular text-rose-600">{fmtTL(r.income_tax + r.stamp_tax)}</td>
                  <td className="px-3 py-2 text-right tabular text-emerald-600">{fmtTL(r.net_salary)}</td>
                  <td className="px-3 py-2 text-right tabular text-emerald-700">{fmtTL(r.take_home)}</td>
                  <td className="px-3 py-2 text-right tabular text-brand-orange">{fmtTL(r.employer_cost)}</td>
                </tr>
              ))}
            </tbody>
            {t && d.rows.length > 0 && (
              <tfoot className="sticky bottom-0 bg-slate-100 font-semibold text-slate-800">
                <tr>
                  <td className="px-3 py-2">TOPLAM</td>
                  <td className="px-3 py-2"></td>
                  <td className="px-3 py-2 text-right tabular">{fmtTL(t.gross)}</td>
                  <td className="px-3 py-2 text-right tabular text-rose-600">{fmtTL(t.sgk_employee + t.unemployment_employee)}</td>
                  <td className="px-3 py-2 text-right tabular text-rose-600">{fmtTL(t.income_tax + t.stamp_tax)}</td>
                  <td className="px-3 py-2 text-right tabular text-emerald-600">{fmtTL(t.net_salary)}</td>
                  <td className="px-3 py-2 text-right tabular text-emerald-700">{fmtTL(t.take_home)}</td>
                  <td className="px-3 py-2 text-right tabular text-brand-orange">{fmtTL(t.employer_cost)}</td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function PersonnelDialog({ person, locs, onClose, onSaved }) {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const isEdit = !!person.id;
  const [form, setForm] = useState({
    name: person.name || "", title: person.title || "", department: person.department || "",
    default_role: person.default_role || "",
    location_id: person.location_id || "",
    monthly_salary: person.monthly_salary ? String(person.monthly_salary) : "",
    salary_type: person.salary_type || "gross", insured: person.insured !== false,
    daily_meal: person.daily_meal ? String(person.daily_meal) : "",
    daily_transport: person.daily_transport ? String(person.daily_transport) : "",
    meal_as_card: !!person.meal_as_card, employer_discount: !!person.employer_discount,
    work_days: person.work_days ? String(person.work_days) : "30",
    account: person.account || "bank", active: person.active !== false, note: person.note || "",
    avatar_url: person.avatar_url || "", bank: person.bank || "",
    national_id: person.national_id || "", phone: person.phone || "", email: person.email || "",
    birth_date: person.birth_date || "", hire_date: person.hire_date || "",
    address: person.address || "", iban: person.iban || "", sgk_no: person.sgk_no || "",
    emergency_contact: person.emergency_contact || "", emergency_phone: person.emergency_phone || "",
    pin: "",
  });
  const [busy, setBusy] = useState(false);
  const revealPin = async () => {
    try { const { data } = await api.get(`/accounting/personnel/${person.id}/pin`); setForm((f) => ({ ...f, pin: data.pin })); toast.success(`PIN: ${data.pin}`); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const [roleOpts, setRoleOpts] = useState([]);
  const [minWage, setMinWage] = useState({ gross: 0, net: 0 });
  useEffect(() => { api.get("/roles").then((r) => setRoleOpts(r.data || [])).catch(() => {}); }, []);
  useEffect(() => { api.get("/accounting/payroll/params").then((r) => { const p = r.data?.params || {}; setMinWage({ gross: Number(p.gross_min_wage_monthly) || 0, net: Number(p.net_min_wage_monthly) || 0 }); }).catch(() => {}); }, []);
  const [docs, setDocs] = useState(person.documents || []);
  const [docCat, setDocCat] = useState("sozlesme");
  const [docBusy, setDocBusy] = useState(false);
  const fileRef = useRef();
  const uploadDoc = async (file) => {
    if (!isEdit) { toast.error("Önce personeli kaydedin, sonra belge ekleyin"); return; }
    setDocBusy(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const up = await api.post("/uploads/document", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const { data } = await api.post(`/accounting/personnel/${person.id}/documents`, { name: file.name, category: docCat, url: up.data.url, path: up.data.path });
      setDocs((d) => [...d, data]); toast.success("Belge eklendi");
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); } finally { setDocBusy(false); }
  };
  const removeDoc = async (id) => {
    try { await api.delete(`/accounting/personnel/${person.id}/documents/${id}`); setDocs((d) => d.filter((x) => x.id !== id)); toast.success("Belge silindi"); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const save = async () => {
    if (!form.name.trim()) { toast.error("Personel adı zorunlu"); return; }
    {
      if (form.national_id.trim().length !== 11) { toast.error("TC Kimlik No 11 haneli olmalı"); return; }
      if (!form.phone.trim()) { toast.error("Telefon zorunlu"); return; }
      if (!form.email.trim() || !form.email.includes("@")) { toast.error("Geçerli bir e-posta zorunlu"); return; }
      if (!form.hire_date) { toast.error("İşe giriş tarihi zorunlu"); return; }
      if (!form.bank) { toast.error("Banka seçimi zorunlu"); return; }
      if (!form.title.trim()) { toast.error("Görev/pozisyon zorunlu"); return; }
    }
    const sal = parseFloat(String(form.monthly_salary).replace(",", ".")) || 0;
    if (sal < 0) { toast.error("Maaş negatif olamaz"); return; }
    setBusy(true);
    try {
      const payload = {
        ...form, monthly_salary: sal, location_id: form.location_id || null,
        daily_meal: parseFloat(String(form.daily_meal).replace(",", ".")) || 0,
        daily_transport: parseFloat(String(form.daily_transport).replace(",", ".")) || 0,
        work_days: Math.min(31, Math.max(1, parseInt(form.work_days, 10) || 30)),
      };
      if (isEdit) await api.put(`/accounting/personnel/${person.id}`, payload);
      else await api.post("/accounting/personnel", payload);
      toast.success(isEdit ? "Personel güncellendi" : "Personel eklendi");
      onSaved();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="personnel-dialog" className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="font-heading">{isEdit ? "Personel Düzenle" : "Yeni Personel"}</DialogTitle>
          <DialogDescription>Kapsamlı personel kartı. Maaş, bordro çalıştırıldığında brütten-nete hesaplanıp <b>işveren maliyeti</b> olarak gidere işlenir.</DialogDescription>
        </DialogHeader>
        <div className="max-h-[70vh] space-y-4 overflow-y-auto pr-1">
          <div className="flex justify-center"><AvatarUpload value={form.avatar_url} name={form.name} onChange={(url) => set("avatar_url", url)} size={64} /></div>

          <div>
            <p className="mb-2 text-[11px] font-bold uppercase tracking-wide text-slate-400">Kimlik & İletişim</p>
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2"><label className="mb-1 block text-xs text-slate-500">Ad Soyad *</label><Input data-testid="personnel-name" value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="Ad Soyad" /></div>
              <div><label className="mb-1 block text-xs text-slate-500">TC Kimlik No *</label><Input data-testid="personnel-national-id" inputMode="numeric" maxLength={11} value={form.national_id} onChange={(e) => set("national_id", e.target.value.replace(/\D/g, ""))} placeholder="11 hane" className="tabular" /></div>
              <div><label className="mb-1 block text-xs text-slate-500">Doğum Tarihi</label><DatePickerTR testid="personnel-birth" value={form.birth_date} onChange={(v) => set("birth_date", v)} /></div>
              <div><label className="mb-1 block text-xs text-slate-500">Telefon *</label><Input data-testid="personnel-phone" value={form.phone} onChange={(e) => set("phone", e.target.value)} placeholder="05xx xxx xx xx" /></div>
              <div><label className="mb-1 block text-xs text-slate-500">E-posta *</label><Input data-testid="personnel-email" type="email" value={form.email} onChange={(e) => set("email", e.target.value)} placeholder="ornek@eposta.com" /></div>
              <div className="col-span-2"><label className="mb-1 block text-xs text-slate-500">Adres</label><Input data-testid="personnel-address" value={form.address} onChange={(e) => set("address", e.target.value)} placeholder="İkamet adresi" /></div>
            </div>
          </div>

          <div>
            <p className="mb-2 text-[11px] font-bold uppercase tracking-wide text-slate-400">İş Bilgileri</p>
            <div className="grid grid-cols-2 gap-3">
              <div><label className="mb-1 block text-xs text-slate-500">Görev / Pozisyon (Sistem Rolü) *</label>
                <select data-testid="personnel-title" value={form.default_role} onChange={(e) => { const rc = e.target.value; const rl = (roleOpts.find((r) => r.code === rc) || {}).label || ""; setForm((f) => ({ ...f, default_role: rc, title: rl })); }} className="h-9 w-full rounded-md border border-input bg-white px-2 text-sm">
                  <option value="">Görev / pozisyon seçin…</option>
                  {roleOpts.filter((r) => !r.disabled).map((r) => <option key={r.code} value={r.code}>{r.label}</option>)}
                </select>
                <p className="mt-1 text-[10px] text-slate-400">Kullanıcı eşleştirmesinde rol buradan otomatik atanır ve sabitlenir.</p>
              </div>
              <div><label className="mb-1 block text-xs text-slate-500">Departman</label><Input data-testid="personnel-department" value={form.department} onChange={(e) => set("department", e.target.value)} placeholder="Satış, Lojistik vb." /></div>
              <div><label className="mb-1 block text-xs text-slate-500">İşe Giriş Tarihi *</label><DatePickerTR testid="personnel-hire" value={form.hire_date} onChange={(v) => set("hire_date", v)} /></div>
              <div><label className="mb-1 block text-xs text-slate-500">SGK Sicil No</label><Input data-testid="personnel-sgk" value={form.sgk_no} onChange={(e) => set("sgk_no", e.target.value)} placeholder="Opsiyonel" /></div>
              <div>
                <label className="mb-1 block text-xs text-slate-500">Depo / Mağaza</label>
                <select data-testid="personnel-location" value={form.location_id} onChange={(e) => set("location_id", e.target.value)} className="h-9 w-full rounded-md border border-input bg-white px-2 text-sm">
                  <option value="">Genel / Atanmamış</option>
                  {(locs || []).map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
                </select>
              </div>
              <label className="col-span-2 flex items-center gap-2 text-sm text-slate-600"><input data-testid="personnel-active" type="checkbox" checked={form.active} onChange={(e) => set("active", e.target.checked)} className="h-4 w-4 rounded border-slate-300" /> Aktif (pasif personel bordroya girmez)</label>
              <div className="col-span-2 rounded-md bg-indigo-50 p-3 ring-1 ring-inset ring-indigo-200">
                <label className="mb-1 block text-xs font-medium text-indigo-700">Personel PIN (Kimlik) — 4-6 hane</label>
                <div className="flex gap-2">
                  <Input data-testid="personnel-pin" inputMode="numeric" maxLength={6} value={form.pin} onChange={(e) => set("pin", e.target.value.replace(/\D/g, ""))} placeholder={person.has_pin ? "•••• (atanmış — değiştirmek için yeni PIN yazın)" : "Örn. 4671"} className="tabular tracking-widest" />
                  {isEdit && person.has_pin && <Button type="button" variant="outline" data-testid="personnel-pin-reveal" onClick={revealPin} className="shrink-0 border-indigo-300 text-indigo-700">Göster</Button>}
                </div>
                <p className="mt-1 text-[10px] text-indigo-500">Ortak rol girişinde vardiyayı açan/aksiyon yapan kişi bu PIN ile kimliğini belirtir (Z-raporunda görünür). Boş bırakılırsa mevcut PIN korunur. PIN tenant içinde benzersiz olmalı.</p>
              </div>
            </div>
          </div>

          <div>
            <p className="mb-2 text-[11px] font-bold uppercase tracking-wide text-slate-400">Maaş & Bordro</p>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="mb-1 block text-xs text-slate-500">Aylık Maaş {curSym()}</label>
                <Input data-testid="personnel-salary" inputMode="decimal" value={form.monthly_salary} onChange={(e) => set("monthly_salary", e.target.value)} placeholder="0,00" className="tabular" />
                {(() => {
                  const s = parseFloat(String(form.monthly_salary).replace(",", ".")) || 0;
                  const isNet = form.salary_type === "net";
                  const threshold = isNet ? minWage.net : minWage.gross;
                  if (s > 0 && threshold > 0 && s < threshold) {
                    return (
                      <p data-testid="personnel-salary-minwage-warn" className="mt-1 text-[10px] font-medium leading-tight text-amber-600">
                        Girilen tutar {isNet ? "asgari net" : "asgari brüt"} ücretin ({curSym()}{threshold.toLocaleString("tr-TR")}) altında. Bordroda SGK kesintileri yasal taban üzerinden hesaplanır; net 0 olarak işlenir.
                      </p>
                    );
                  }
                  return null;
                })()}
              </div>
              <div>
                <label className="mb-1 block text-xs text-slate-500">Maaş Türü</label>
                <select data-testid="personnel-salary-type" value={form.salary_type} onChange={(e) => set("salary_type", e.target.value)} className="h-9 w-full rounded-md border border-input bg-white px-2 text-sm">
                  <option value="gross">Brüt (standart bordro)</option>
                  <option value="net">Net (ele geçen üzerinden)</option>
                </select>
              </div>
              <div><label className="mb-1 block text-xs text-slate-500">Günlük Yol {curSym()}</label><Input data-testid="personnel-daily-transport" inputMode="decimal" value={form.daily_transport} onChange={(e) => set("daily_transport", e.target.value)} placeholder="0,00" className="tabular" /></div>
              <div><label className="mb-1 block text-xs text-slate-500">Günlük Yemek {curSym()} (istisna ≤ {158})</label><Input data-testid="personnel-daily-meal" inputMode="decimal" value={form.daily_meal} onChange={(e) => set("daily_meal", e.target.value)} placeholder="0,00" className="tabular" /></div>
              <div><label className="mb-1 block text-xs text-slate-500">Çalışma Günü (ay)</label><Input data-testid="personnel-work-days" inputMode="numeric" value={form.work_days} onChange={(e) => set("work_days", e.target.value.replace(/\D/g, ""))} placeholder="30" className="tabular" /></div>
              <label className="flex items-center gap-2 self-end text-sm text-slate-600"><input data-testid="personnel-meal-card" type="checkbox" checked={form.meal_as_card} onChange={(e) => set("meal_as_card", e.target.checked)} className="h-4 w-4 rounded border-slate-300" /> Yemek kartı (nakit değil)</label>
              <label className="flex items-center gap-2 text-sm text-slate-600"><input data-testid="personnel-insured" type="checkbox" checked={form.insured} onChange={(e) => set("insured", e.target.checked)} className="h-4 w-4 rounded border-slate-300" /> SGK'lı (kesinti uygulanır)</label>
              <label className="flex items-center gap-2 text-sm text-slate-600"><input data-testid="personnel-employer-discount" type="checkbox" checked={form.employer_discount} onChange={(e) => set("employer_discount", e.target.checked)} className="h-4 w-4 rounded border-slate-300" /> 5 puanlık SGK işveren indirimi</label>
            </div>
          </div>

          <div>
            <p className="mb-2 text-[11px] font-bold uppercase tracking-wide text-slate-400">Ödeme</p>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="mb-1 block text-xs text-slate-500">Ödeme Hesabı</label>
                <select data-testid="personnel-account" value={form.account} onChange={(e) => set("account", e.target.value)} className="h-9 w-full rounded-md border border-input bg-white px-2 text-sm">
                  <option value="bank">Banka</option>
                  <option value="cash">Nakit</option>
                </select>
              </div>
              <div>
                <label className="mb-1 block text-xs text-slate-500">Banka *</label>
                <select data-testid="personnel-bank" value={form.bank} onChange={(e) => set("bank", e.target.value)} className="h-9 w-full rounded-md border border-input bg-white px-2 text-sm">
                  <option value="">Banka seçin…</option>
                  {BANKS.map((b) => <option key={b.code} value={b.code}>{b.name}</option>)}
                </select>
                {form.bank && <div className="mt-1.5"><BankBadge code={form.bank} /></div>}
              </div>
              <div className="col-span-2"><label className="mb-1 block text-xs text-slate-500">IBAN</label><Input data-testid="personnel-iban" value={form.iban} onChange={(e) => set("iban", e.target.value)} placeholder="TR.." /></div>
            </div>
          </div>

          {isEdit && (
            <div>
              <p className="mb-2 text-[11px] font-bold uppercase tracking-wide text-slate-400">Özlük Dosyası (sözleşme / kimlik / SGK)</p>
              <div className="flex items-center gap-2">
                <select value={docCat} onChange={(e) => setDocCat(e.target.value)} className="h-9 rounded-md border border-input bg-white px-2 text-sm">
                  <option value="sozlesme">Sözleşme</option><option value="kimlik">Kimlik</option><option value="sgk">SGK</option><option value="diploma">Diploma</option><option value="diger">Diğer</option>
                </select>
                <input ref={fileRef} type="file" className="hidden" onChange={(e) => e.target.files?.[0] && uploadDoc(e.target.files[0])} />
                <Button type="button" variant="outline" size="sm" disabled={docBusy} onClick={() => fileRef.current?.click()} data-testid="personnel-doc-upload">{docBusy ? "Yükleniyor…" : "Belge Ekle"}</Button>
              </div>
              {docs.length > 0 && (
                <div className="mt-2 space-y-1">
                  {docs.map((d) => (
                    <div key={d.id} className="flex items-center justify-between rounded-md bg-slate-50 px-2 py-1.5 text-xs">
                      <span className="truncate text-slate-600">{d.category} · {d.name}</span>
                      <div className="flex items-center gap-2">
                        <a href={d.url} target="_blank" rel="noreferrer" className="text-brand-green hover:underline">Aç</a>
                        <button onClick={() => removeDoc(d.id)} className="text-slate-400 hover:text-red-500"><Trash2 className="h-3.5 w-3.5" /></button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          <div>
            <p className="mb-2 text-[11px] font-bold uppercase tracking-wide text-slate-400">Acil Durum & Not</p>
            <div className="grid grid-cols-2 gap-3">
              <div><label className="mb-1 block text-xs text-slate-500">Acil Durumda Aranacak</label><Input data-testid="personnel-emergency-contact" value={form.emergency_contact} onChange={(e) => set("emergency_contact", e.target.value)} placeholder="Ad Soyad / yakınlık" /></div>
              <div><label className="mb-1 block text-xs text-slate-500">Acil Durum Telefonu</label><Input data-testid="personnel-emergency-phone" value={form.emergency_phone} onChange={(e) => set("emergency_phone", e.target.value)} placeholder="05xx xxx xx xx" /></div>
              <div className="col-span-2"><label className="mb-1 block text-xs text-slate-500">Not</label><Input data-testid="personnel-note" value={form.note} onChange={(e) => set("note", e.target.value)} placeholder="Opsiyonel" /></div>
            </div>
          </div>

          <Button data-testid="personnel-save" onClick={save} disabled={busy} className="w-full bg-brand-green text-white hover:bg-brand-green-600">{busy ? "Kaydediliyor..." : "Kaydet"}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
